// API Client Service - OOP Pattern
import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { IApiClient } from '../interfaces/IApiClient';
import { useApiLoadingStore } from './ApiLoadingStore';

export class ApiClient implements IApiClient {
  private client: AxiosInstance;
  private authToken: string | null = null;
  private readonly getCache = new Map<string, { data: unknown; expiresAt: number }>();
  private readonly defaultGetCacheTtlMs = 30000;

  constructor(baseURL: string = 'http://localhost:3000') {
    this.client = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor para adicionar token em todas as requisições
    this.client.interceptors.request.use((config) => {
      if (this.isLoadingEnabled(config)) {
        useApiLoadingStore.getState().requestStarted();
      }

      if (this.authToken) {
        config.headers.Authorization = `Bearer ${this.authToken}`;
      }

      return config;
    });

    // Interceptor para tratar erros
    this.client.interceptors.response.use(
      (response) => {
        if (this.isLoadingEnabled(response.config)) {
          useApiLoadingStore.getState().requestFinished();
        }

        return response;
      },
      (error: AxiosError) => {
        if (this.isLoadingEnabled(error.config)) {
          useApiLoadingStore.getState().requestFinished();
        }

        if (error.response?.status === 401) {
          this.clearAuthToken();
          window.location.href = '/login';
        }

        throw error;
      }
    );
  }

  private isLoadingEnabled(config?: InternalAxiosRequestConfig | any): boolean {
    if (!config) {
      return true;
    }

    return config.headers?.['x-skip-loading'] !== true;
  }

  private shouldUseGetCache(config?: any): boolean {
    if (!config) {
      return true;
    }

    return config.headers?.['x-skip-cache'] !== true;
  }

  private getCacheTtlMs(config?: any): number {
    const ttlFromHeader = Number(config?.headers?.['x-cache-ttl-ms']);
    if (!Number.isNaN(ttlFromHeader) && ttlFromHeader > 0) {
      return ttlFromHeader;
    }

    return this.defaultGetCacheTtlMs;
  }

  private buildGetCacheKey(url: string, config?: any): string {
    const params = config?.params ? JSON.stringify(config.params) : '';
    const token = this.authToken || localStorage.getItem('authToken') || 'anonymous';
    return `${token}::${url}::${params}`;
  }

  private clearGetCache(): void {
    this.getCache.clear();
  }

  async get<T>(url: string, config?: any): Promise<T> {
    try {
      if (this.shouldUseGetCache(config)) {
        const cacheKey = this.buildGetCacheKey(url, config);
        const cached = this.getCache.get(cacheKey);
        if (cached && cached.expiresAt > Date.now()) {
          return cached.data as T;
        }

        const response = await this.client.get<T>(url, config);
        this.getCache.set(cacheKey, {
          data: response.data,
          expiresAt: Date.now() + this.getCacheTtlMs(config),
        });
        return response.data;
      }

      const response = await this.client.get<T>(url, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async post<T>(url: string, data?: any, config?: any): Promise<T> {
    try {
      const response = await this.client.post<T>(url, data, config);
      this.clearGetCache();
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async put<T>(url: string, data?: any, config?: any): Promise<T> {
    try {
      const response = await this.client.put<T>(url, data, config);
      this.clearGetCache();
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async delete<T>(url: string, config?: any): Promise<T> {
    try {
      const response = await this.client.delete<T>(url, config);
      this.clearGetCache();
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  setAuthToken(token: string): void {
    this.authToken = token;
    localStorage.setItem('authToken', token);
    this.clearGetCache();
  }

  clearAuthToken(): void {
    this.authToken = null;
    localStorage.removeItem('authToken');
    this.clearGetCache();
  }

  getAuthToken(): string | null {
    return this.authToken || localStorage.getItem('authToken');
  }

  private handleError(error: any): Error {
    if (error?.code === 'ECONNABORTED') {
      return new Error('Tempo limite excedido ao aguardar resposta do servidor');
    }

    if (error.response) {
      const message = error.response.data?.message || error.message;
      return new Error(message);
    }
    return error;
  }
}
